import React, { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Copy, Loader2, Link2, Trash2, Pencil, BarChart3, ExternalLink, Search, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

export default function AdminShortLinks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ target_url: '', slug: '', title: '', active: true });
  const [saving, setSaving] = useState(false);
  const [clicksOpen, setClicksOpen] = useState(false);
  const [clicksData, setClicksData] = useState(null);
  const { toast } = useToast();

  const load = async (search = '') => {
    setLoading(true);
    try {
      const data = await adminApi.listShortLinks(search);
      setItems(data || []);
    } catch { toast({ title: 'Failed to load short links' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ target_url: '', slug: '', title: '', active: true });
    setOpen(true);
  };
  const openEdit = (sl) => {
    setEditing(sl);
    setForm({ target_url: sl.target_url, slug: sl.slug, title: sl.title || '', active: sl.active !== false });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.target_url.trim()) return toast({ title: 'Destination URL is required' });
    setSaving(true);
    try {
      if (editing) await adminApi.updateShortLink(editing.id, form);
      else await adminApi.createShortLink({ ...form, slug: form.slug || undefined });
      setOpen(false);
      await load(q);
      toast({ title: editing ? 'Short link updated' : 'Short link created' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again.' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (sl) => {
    if (!window.confirm(`Delete short link ${sl.short_url}?`)) return;
    try {
      await adminApi.deleteShortLink(sl.id);
      await load(q);
      toast({ title: 'Short link deleted' });
    } catch { toast({ title: 'Delete failed' }); }
  };

  const toggleActive = async (sl) => {
    try {
      await adminApi.updateShortLink(sl.id, { active: !sl.active });
      await load(q);
    } catch { toast({ title: 'Update failed' }); }
  };

  const openClicks = async (sl) => {
    setClicksData({ short_link: sl, clicks: null });
    setClicksOpen(true);
    try {
      const d = await adminApi.shortLinkClicks(sl.id);
      setClicksData(d);
    } catch { toast({ title: 'Failed to load clicks' }); }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copied', description: text });
    } catch { toast({ title: 'Copy failed' }); }
  };

  const fmt = (v) => v ? new Date(v).toLocaleString() : '';

  return (
    <div data-testid="admin-shortlinks-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-slate-900">Short Links</h1>
          <p className="text-slate-600 mt-1">Create branded <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">theloanalliance.com/track/xyz</code> links for affiliate offers. All visitor URL params are forwarded to the destination, and the affiliate URL is hidden from users (address bar keeps showing your domain).</p>
        </div>
        <Button onClick={openNew} data-testid="new-shortlink-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="w-4 h-4 mr-1.5" /> New Short Link
        </Button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-10" placeholder="Search by slug, URL or title…" />
          {q && (
            <button type="button" onClick={() => { setQ(''); load(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button type="submit" variant="outline">Filter</Button>
      </form>

      {loading ? (
        <div className="flex justify-center py-14 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-white border border-slate-200 p-10 text-center text-slate-500">
          <Link2 className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          No short links yet. Click "New Short Link" to create one.
        </div>
      ) : (
        <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase tracking-wider text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Short URL</th>
                  <th className="text-left px-4 py-3">Destination</th>
                  <th className="text-left px-4 py-3">Title</th>
                  <th className="text-right px-4 py-3">Clicks</th>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-left px-4 py-3">Active</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((sl) => (
                  <tr key={sl.id} data-testid={`shortlink-row-${sl.slug}`} className="border-b border-slate-100 hover:bg-emerald-50/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <code data-testid={`shortlink-url-${sl.slug}`} className="text-emerald-700 font-semibold">{sl.short_url}</code>
                        <button data-testid={`shortlink-copy-${sl.slug}`} onClick={() => copy(sl.short_url)} className="p-1 text-slate-400 hover:text-emerald-700" title="Copy short URL">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <a href={sl.target_url} target="_blank" rel="noopener" className="text-slate-700 hover:text-emerald-700 truncate inline-flex items-center gap-1 max-w-full">
                        <span className="truncate">{sl.target_url}</span> <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{sl.title || <span className="text-slate-400 italic">—</span>}</td>
                    <td className="px-4 py-3 text-right font-semibold" data-testid={`shortlink-clicks-count-${sl.slug}`}>{sl.clicks || 0}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmt(sl.created_at)}</td>
                    <td className="px-4 py-3">
                      <Switch data-testid={`shortlink-active-${sl.slug}`} checked={sl.active} onCheckedChange={() => toggleActive(sl)} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" data-testid={`shortlink-clicklog-${sl.slug}`} onClick={() => openClicks(sl)} title="Click log">
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" data-testid={`shortlink-edit-${sl.slug}`} onClick={() => openEdit(sl)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" data-testid={`shortlink-delete-${sl.slug}`} onClick={() => handleDelete(sl)} className="text-red-600 hover:bg-red-50 border-red-200"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Short Link' : 'New Short Link'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Destination URL (affiliate link)</Label>
              <Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                     data-testid="shortlink-target-input" className="mt-1 h-10" placeholder="https://partner.com/offer?campaign=abc" />
              <p className="mt-1 text-[11px] text-slate-500">Visitor's URL params (name, email, phone, utm_*, sub_id, etc.) are appended to this URL on every click.</p>
            </div>
            <div>
              <Label className="text-xs">Custom slug (optional)</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                     data-testid="shortlink-slug-input" className="mt-1 h-10" placeholder="Leave blank for auto — e.g. hdfc-cc" />
              <p className="mt-1 text-[11px] text-slate-500">3–32 chars, letters/numbers/_/- only. Leave blank to get a random 6-char code.</p>
            </div>
            <div>
              <Label className="text-xs">Internal title (optional)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 h-10" placeholder="e.g. HDFC Credit Card CPA" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label className="text-sm">Active — redirect visitors</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="shortlink-save-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Save Changes' : 'Create Short Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clicks dialog */}
      <Dialog open={clicksOpen} onOpenChange={setClicksOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="shortlink-clicklog-dialog">
          <DialogHeader>
            <DialogTitle>Click Log — <code className="text-emerald-700">{clicksData?.short_link?.short_url}</code></DialogTitle>
          </DialogHeader>
          {clicksData?.clicks === null ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (clicksData?.clicks || []).length === 0 ? (
            <div className="py-8 text-center text-slate-500">No clicks yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {clicksData.clicks.map((c) => (
                <li key={c.id} className="py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-slate-500">{fmt(c.created_at)} · <code>{c.session_id || 'anon'}</code></div>
                    <div className="text-xs text-slate-400 truncate max-w-[240px]">{c.ip}</div>
                  </div>
                  <div className="mt-1 text-sm text-slate-800 truncate">→ <a href={c.final_url} target="_blank" rel="noopener" className="text-emerald-700 hover:underline">{c.final_url}</a></div>
                  {c.params && Object.keys(c.params).length > 0 && (
                    <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                      {Object.entries(c.params).map(([k, v]) => (
                        <span key={k}><span className="text-slate-400">{k}:</span> <span className="text-slate-700 font-medium">{String(v)}</span></span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
